import { useEffect, useState } from "react";

export default function Hero() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setTimeout(() => setLoaded(true), 100); }, []);

  return (
    <section id="home" className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-red-950 via-blue-950 to-blue-900">
      {/* Grid background */}
      <div className="absolute inset-0 hero-grid opacity-60" />

      {/* Gradient overlays */}
      <div className="absolute inset-0 bg-gradient-to-br from-red-950/60 via-blue-950 to-blue-900" style={{ opacity: 0.92 }} />
      <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-red-600/10 to-transparent" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-red-500/10 rounded-full blur-3xl" />
      <div className="absolute top-32 right-1/4 w-64 h-64 bg-blue-500/5 rounded-full blur-3xl" />

      {/* Floating construction shapes */}
      <div className="absolute right-8 top-1/4 w-64 h-64 opacity-10 animate-float" style={{ animationDelay: '0s' }}>
        <svg viewBox="0 0 200 200" fill="none">
          <rect x="10" y="120" width="40" height="70" fill="#dc2626"/>
          <rect x="60" y="80" width="40" height="110" fill="#1e40af"/>
          <rect x="110" y="50" width="40" height="140" fill="#dc2626"/>
          <rect x="160" y="100" width="30" height="90" fill="#1e3a8a"/>
        </svg>
      </div>
      <div className="absolute right-1/4 top-16 w-4 h-4 bg-red-400 rounded-full opacity-60 animate-pulse-slow"/>
      <div className="absolute right-1/3 bottom-1/4 w-3 h-3 bg-blue-400 rounded-full opacity-40 animate-pulse-slow" style={{ animationDelay: '1s' }}/>
      <div className="absolute left-1/4 top-1/3 w-2 h-2 bg-red-300 rounded-full opacity-30 animate-pulse-slow" style={{ animationDelay: '2s' }}/>

      <div className="relative max-w-7xl mx-auto px-6 py-32 grid lg:grid-cols-2 gap-16 items-center">
        {/* Left */}
        <div>
          <div className={`inline-flex items-center gap-2 bg-red-600/20 border border-red-500/30 text-red-300 
            text-xs font-semibold tracking-widest uppercase px-4 py-2 rounded-full mb-8 transition-all duration-700
            ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="w-1.5 h-1.5 bg-red-400 rounded-full animate-pulse-slow"/>
            Building Excellence Since 2021
          </div>

          <h1 className={`transition-all duration-700 delay-100 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontWeight: 700, fontSize: 'clamp(3rem,7vw,6rem)', lineHeight: 1.05, color: 'white' }}>
            BUILDING<br/>
            <span style={{ WebkitTextStroke: '2px rgba(255,255,255,0.3)', color: 'transparent' }}>TOMORROW'S</span><br/>
            <span className="text-red-400">SKYLINE</span>
          </h1>

          <p className={`text-blue-200/80 text-lg mt-6 max-w-md leading-relaxed transition-all duration-700 delay-200
            ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
            style={{ fontFamily: "'DM Sans', sans-serif" }}>
            NexEdge Infra delivers world-class infrastructure solutions — from high-rise construction and civil engineering to smart urban development across India.
          </p>

          <div className={`flex flex-wrap gap-4 mt-10 transition-all duration-700 delay-300
            ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            <a href="#projects"
              className="btn-primary bg-gradient-to-r from-red-900 to-blue-900 hover:from-red-800 hover:to-blue-800 text-white px-8 py-4 rounded-xl font-semibold text-base transition-all duration-300 shadow-xl shadow-red-900/40 flex items-center gap-2">
              View Projects
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3"/>
              </svg>
            </a>
            <a href="#contact"
              className="border-2 border-white/20 hover:border-white/60 text-white px-8 py-4 rounded-xl font-semibold text-base transition-all duration-300 flex items-center gap-2">
              Free Consultation
            </a>
          </div>

          {/* Stats row - Updated with correct company data */}
          <div className={`flex gap-10 mt-14 pt-10 border-t border-white/10 transition-all duration-700 delay-500
            ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {[
              { val: "15+", label: "Projects Done" },
              { val: "5", label: "Years Experience" },
              { val: "20+", label: "Happy Clients" },
            ].map(s => (
              <div key={s.label}>
                <div className="text-red-400 font-bold text-3xl" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  {s.val}
                </div>
                <div className="text-blue-300/60 text-xs tracking-widest uppercase mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Right – isometric building illustration */}
        <div className={`hidden lg:flex justify-center transition-all duration-1000 delay-400
          ${loaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}>
          <div className="relative w-96 h-96">
            <svg viewBox="0 0 400 420" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full drop-shadow-2xl">
              {/* Building 1 – tall center with red-blue gradient */}
              <g>
                <polygon points="160,40 260,40 260,340 160,340" fill="#991b1b" opacity="0.9"/>
                <polygon points="260,40 310,70 310,370 260,340" fill="#1e3a8a" opacity="0.9"/>
                <polygon points="110,70 160,40 260,40 210,70" fill="#dc2626" opacity="0.9"/>
                {/* Windows - alternating red and blue */}
                {[80,110,140,170,200,230,260,290].map((y,i) => (
                  <g key={i}>
                    <rect x="175" y={y} width="20" height="18" fill="#ef4444" opacity="0.7" rx="1"/>
                    <rect x="205" y={y} width="20" height="18" fill="#3b82f6" opacity="0.5" rx="1"/>
                    <rect x="235" y={y} width="18" height="18" fill="#b91c1c" opacity="0.6" rx="1"/>
                  </g>
                ))}
                {/* Antenna */}
                <line x1="210" y1="40" x2="210" y2="10" stroke="#ef4444" strokeWidth="3" strokeLinecap="round"/>
                <circle cx="210" cy="8" r="3" fill="#ef4444"/>
              </g>

              {/* Building 2 – shorter left */}
              <g>
                <polygon points="60,120 130,120 130,340 60,340" fill="#7f1d1d" opacity="0.85"/>
                <polygon points="130,120 165,140 165,360 130,340" fill="#1e3a8a" opacity="0.85"/>
                <polygon points="25,140 60,120 130,120 95,140" fill="#991b1b" opacity="0.85"/>
                {[155,180,205,230,255,280,305].map((y,i) => (
                  <g key={i}>
                    <rect x="72" y={y} width="16" height="14" fill="#dc2626" opacity="0.5" rx="1"/>
                    <rect x="96" y={y} width="16" height="14" fill="#3b82f6" opacity="0.4" rx="1"/>
                  </g>
                ))}
              </g>

              {/* Building 3 – right */}
              <g>
                <polygon points="280,150 340,150 340,340 280,340" fill="#b91c1c" opacity="0.8"/>
                <polygon points="340,150 375,172 375,362 340,340" fill="#1e40af" opacity="0.8"/>
                <polygon points="245,172 280,150 340,150 305,172" fill="#dc2626" opacity="0.8"/>
                {[175,200,225,250,275,300,320].map((y,i) => (
                  <g key={i}>
                    <rect x="290" y={y} width="14" height="12" fill="#60a5fa" opacity="0.5" rx="1"/>
                    <rect x="312" y={y} width="14" height="12" fill="#ef4444" opacity="0.4" rx="1"/>
                  </g>
                ))}
              </g>

              {/* Ground with red-blue gradient */}
              <polygon points="25,340 95,370 375,370 375,362 305,332 25,332" fill="#1e1b4b" opacity="0.9"/>
              <polygon points="25,332 95,362 95,370 25,340" fill="#2e1065"/>
              <rect x="25" y="332" width="370" height="8" fill="url(#groundGradient)"/>
              <defs>
                <linearGradient id="groundGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="#991b1b"/>
                  <stop offset="100%" stopColor="#1e3a8a"/>
                </linearGradient>
              </defs>

              {/* Crane */}
              <line x1="330" y1="10" x2="330" y2="150" stroke="#dc2626" strokeWidth="2.5"/>
              <line x1="290" y1="10" x2="380" y2="10" stroke="#dc2626" strokeWidth="2.5"/>
              <line x1="380" y1="10" x2="380" y2="40" stroke="#ef4444" strokeWidth="1.5" strokeDasharray="4,3"/>
              <circle cx="380" cy="42" r="3" fill="#ef4444"/>
            </svg>

            {/* Glow ring */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-64 h-12 bg-red-500/20 blur-2xl rounded-full"/>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-blue-300/50">
        <span className="text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-12 bg-gradient-to-b from-red-400/50 to-transparent"/>
      </div>
    </section>
  );
}